import type { AnyAction } from 'redux';
import type { ThunkAction } from 'redux-thunk';

import { nuc } from '../../utils';
import { get } from '../api';
import type {
  Dependency,
  DependencyQueryWithFilters,
  DependencyScope,
  ProjectDependencyScope,
  ReleaseGroupDependencyScope,
} from '../../../shared/dependenciesV2';
import type { ReduxAction, SyncReduxAction } from '../../types';
import type { ReduxStore } from '../store';

import initialState from './initialState';
import { getBuildWatchInterval, getFetchDependenciesQuery, getScope, getWatchedRevisions } from './selectors';
import type { HydrateQueryState } from './types';

// Slightly increased from the timeout previously used in DependencyStore
const DEFAULT_TIMEOUT = 1500;

const endpoints = Object.freeze({
  projectDependencies: (scope: ProjectDependencyScope) => `/api/v2/revisions/${nuc(scope.revision)}/dependencies`,
  releaseGroupDependencies: (scope: ReleaseGroupDependencyScope) => `/api/v2/release-groups/${scope.projectGroupId}/releases/${scope.projectGroupReleaseId}/dependencies`,
  resolve: (revision: string) => `/api/revisions/${nuc(revision)}/resolve`,
  projectLicenses: (scope: ProjectDependencyScope) => `/api/revisions/${nuc(scope.revision)}/deeplicenses`,
  releaseGroupLicenses: (scope: ReleaseGroupDependencyScope) => `/api/project_group/${scope.projectGroupId}/release/${scope.projectGroupReleaseId}/licenses`,
  projectPackageManagers: (scope: ProjectDependencyScope) => `/api/v2/revisions/${nuc(scope.revision)}/dependencies/package-managers`,
  releaseGroupPackageManagers: (scope: ReleaseGroupDependencyScope) => `/api/v2/release-groups/${scope.projectGroupId}/releases/${scope.projectGroupReleaseId}/dependencies/package-managers`,
  releaseGroupRootProjects: (scope: ReleaseGroupDependencyScope) => `/api/v2/release-groups/${scope.projectGroupId}/releases/${scope.projectGroupReleaseId}/root-projects`,
  revisions: '/api/revisions',
});

export const actionTypes = Object.freeze({
  RESET: 'DEPENDENCIESV2_RESET',
  PRE_FETCH_LOADING: 'DEPENDENCIESV2_PRE_FETCH_LOADING',
  SET_PAGE: 'DEPENDENCIESV2_PAGE',
  SET_COUNT: 'DEPENDENCIESV2_COUNT',
  SET_SCOPE: 'DEPENDENCIESV2_SET_SCOPE',
  SET_FILTERS: 'DEPENDENCIESV2_SET_FILTERS',
  SET_IS_ORG_MEMBER: 'DEPENDENCIESV2_SET_IS_ORG_MEMBER',
  HYDRATE_STATE: 'DEPENDENCIESV2_HYDRATE_STATE',
  FETCH_DEPENDENCIES: 'DEPENDENCIESV2_FETCH_DEPENDENCIES',
  UPDATE_DEPENDENCY: 'DEPENDENCIESV2_UPDATE_DEPENDENCY',
  CORRECT_DEPENDENCY: 'DEPENDENCIESV2_CORRECT_DEPENDENCY',
  RESOLVE_DEPENDENCY: 'DEPENDENCIESV2_RESOLVE_DEPENDENCY',
  FETCH_LICENSES: 'DEPENDENCIESV2_FETCH_LICENSES',
  FETCH_PACKAGE_MANAGERS: 'DEPENDENCIESV2_FETCH_PACKAGE_MANAGERS',
  FETCH_ROOT_PROJECTS: 'DEPENDENCIESV2_FETCH_ROOT_PROJECTS',
  BUILD_WATCH_FETCH_REVISIONS: 'DEPENDENCIESV2_BUILD_WATCH_FETCH_REVISIONS',
  BUILD_WATCH_ADD_REVISION: 'DEPENDENCIESV2_BUILD_WATCH_ADD_REVISION',
  BUILD_WATCH_REMOVE_REVISION: 'DEPENDENCIESV2_BUILD_WATCH_REMOVE_REVISION',
  BUILD_WATCH_UPDATE_INTERVAL: 'DEPENDENCIESV2_BUILD_WATCH_UPDATE_INTERVAL',
  BUILD_WATCH_START: 'DEPENDENCIESV2_BUILD_WATCH_START',
  BUILD_WATCH_STOP: 'DEPENDENCIESV2_BUILD_WATCH_STOP',
  BUILD_WATCH_CLEAR: 'DEPENDENCIESV2_BUILD_WATCH_CLEAR',
});

/**
 * Resets state back to initial state - also cancels any build watch intervals
 */
export const resetState = () => ({
  type: actionTypes.RESET,
});

/**
 * Initiates a loading state to counteract delays from debounce
 */
export const preFetchLoading = () => ({
  type: actionTypes.PRE_FETCH_LOADING,
});

/**
 * Sets the current dependency scope.
 */
export const setScope = (scope: DependencyScope): SyncReduxAction<{ scope: DependencyScope }> => ({
  type: actionTypes.SET_SCOPE,
  scope,
});

/**
 * Action for updating the pagination page.
 */
export const setPage = (page: number): SyncReduxAction<{ page: number }> => ({
  type: actionTypes.SET_PAGE,
  page,
});

/**
 * Action for updating the pagination count.
 */
export const setCount = (count: number): SyncReduxAction<{ count: number }> => ({
  type: actionTypes.SET_COUNT,
  count,
});

/**
 * Action for changing filters
 */
export const setFilters = (
  filters: DependencyQueryWithFilters,
): SyncReduxAction<{ filters: DependencyQueryWithFilters }> => ({
  type: actionTypes.SET_FILTERS,
  filters,
});

export const resetFilters = () => ({
  type: actionTypes.SET_FILTERS,
  filters: initialState.filters,
});

/**
 * Action for setting isOrgMember state.
 */
export const setIsOrgMember = (
  isOrgMember: boolean,
): SyncReduxAction<{ isOrgMember: boolean }> => ({
  type: actionTypes.SET_IS_ORG_MEMBER,
  isOrgMember,
});

/**
 * Hydrate state from URL - sets revision, filters, and pagination
 */
export const hydrateState = (
  query: HydrateQueryState,
): SyncReduxAction<{ query: HydrateQueryState }> => ({
  type: actionTypes.HYDRATE_STATE,
  query,
});

/**
 * Fetch Dependencies - builds query from state, no params required
 */
export const fetchDependencies = (): ThunkAction<void, ReduxStore, never, AnyAction> =>
  (dispatch, getState) => {
    const state = getState();
    const scope = getScope(state);
    const query = getFetchDependenciesQuery(state);

    if (!scope) {
      return;
    }

    dispatch(fetchDependenciesAction(scope, query));
  };

/**
 * This helper inspects the DepsV2/V3 query params and checks if the title is a
 * revision locator. If it is, then we want to filter by locator (partial match)
 * instead of the dependency's title (partial match).
 */
export function convertSearchByLocatorQuery(query: DependencyQueryWithFilters): DependencyQueryWithFilters {
  const title = query.title;
  if (!title) {
    return query;
  }

  // If the title doesn't contain hints of being a locator, return the query as is.
  if (!(title.includes('+') || title.includes('$'))) {
    return query;
  }

  // Add "locators" to the query and remove "title".
  const newQuery = {
    ...query,
    locators: [title],
  };
  delete newQuery.title;

  return newQuery;
}

/**
 * Action for fetching dependencies.
 */
export const fetchDependenciesAction = (scope: DependencyScope, query: DependencyQueryWithFilters): ReduxAction => ({
  type: actionTypes.FETCH_DEPENDENCIES,
  meta: { timestamp: Date.now() },
  payload: async () => {
    const convertedQuery = convertSearchByLocatorQuery(query);
    if (scope.scope === 'project') {
      return get(endpoints.projectDependencies(scope), true, convertedQuery, { encodeQuery: true });
    }
    if (scope.scope === 'releaseGroup') {
      return get(endpoints.releaseGroupDependencies(scope), true, convertedQuery, { encodeQuery: true });
    }
    // This action doesn't actually support fetching global dependencies.
    return new Response();
  },
});

export const updateDependency = (
  locator: string,
  dependency: Partial<Dependency>,
): SyncReduxAction<{ locator: string, dependency: Partial<Dependency> }> => ({
  type: actionTypes.UPDATE_DEPENDENCY,
  locator,
  dependency,
});

/**
 * Action to resolve/rebuild dependency
 */
const resolveDependencyAction = (revision: string, rebuild: boolean = false): ReduxAction => ({
  type: actionTypes.RESOLVE_DEPENDENCY,
  payload: async () => get(endpoints.resolve(revision), true, { rebuild }),
});

/**
 * Thunk to resolve a dependency
 */
export const resolveDependency = (
  revision: string, shouldWatch: boolean = false, rebuild: boolean = false,
): ThunkAction<void, ReduxStore, never, AnyAction> =>
  async (dispatch) => {
    await dispatch(resolveDependencyAction(revision, rebuild));

    if (shouldWatch) {
      await dispatch(addAndFetchBuildWatchRevision(revision));
    }
  };

/**
 * Thunk to build a dependency
 */
export const buildDependency = (
  revision: string, shouldWatch: boolean = false,
) => resolveDependency(revision, shouldWatch, true);

/**
 * Action for fetching licenses.
 */
export const fetchLicenses = (scope: DependencyScope): ReduxAction => ({
  type: actionTypes.FETCH_LICENSES,
  payload: async () => {
    if (scope.scope === 'project') {
      return get(endpoints.projectLicenses(scope), true);
    }
    if (scope.scope === 'releaseGroup') {
      return get(endpoints.releaseGroupLicenses(scope), true);
    }
    // This action doesn't actually support fetching licenses for global dependencies.
    return new Response();
  },
});

/**
 * Action for fetching package managers.
 */
export const fetchPackageManagersAction = (scope: DependencyScope): ReduxAction => ({
  type: actionTypes.FETCH_PACKAGE_MANAGERS,
  payload: async () => {
    if (scope.scope === 'project') {
      return get(endpoints.projectPackageManagers(scope), true);
    }
    if (scope.scope === 'releaseGroup') {
      return get(endpoints.releaseGroupPackageManagers(scope), true);
    }
    // This action doesn't actually support fetching package managers for global dependencies.
    return new Response();
  },
});

/**
 * Action for fetching release group root projects.
 */
export const fetchRootProjectsAction = (scope: ReleaseGroupDependencyScope): ReduxAction => ({
  type: actionTypes.FETCH_ROOT_PROJECTS,
  payload: async () => {
    return get(endpoints.releaseGroupRootProjects(scope), true);
  },
});

/**
 * Action to adds a revision to the build watch list
 */
export const addRevisionToBuildWatch = (revision: string): SyncReduxAction<{ revision: string }> => ({
  type: actionTypes.BUILD_WATCH_ADD_REVISION,
  revision,
});

/**
 * Action to removes a revision from the build watch list
 */
export const removeRevisionFromBuildWatch = (revision: string): SyncReduxAction<{ revision: string }> => ({
  type: actionTypes.BUILD_WATCH_REMOVE_REVISION,
  revision,
});

/**
 * Update the build watch interval - used to cancel the interval
 */
// eslint-disable-next-line no-undef
export const updateBuildWatchInterval = (interval: NodeJS.Timeout): SyncReduxAction<{ interval: NodeJS.Timeout }> => ({
  type: actionTypes.BUILD_WATCH_UPDATE_INTERVAL,
  interval,
});

/**
 * Action to stop build watch interval and clear watch list
 */
export const stopBuildWatchAction = () => ({
  type: actionTypes.BUILD_WATCH_STOP,
});

/**
 * Thunk to start build action. If there is no interval, it will create a new one and start the watch
 */
export const startBuildWatch = (intervalTime: number): ThunkAction<void, ReduxStore, never, AnyAction> =>
  (dispatch, getState) => {
    const state = getState();
    const interval = getBuildWatchInterval(state);

    if (!interval) {
      const newInterval = setInterval(
        () => dispatch(fetchBuildWatchRevisions()),
        intervalTime,
      );
      return dispatch(updateBuildWatchInterval(newInterval));
    }

    return Promise.resolve();
  };

/**
 * Thunk to fetch build watch revisions. Dispatches a fetch action if there are revisions to watch
 * Otherwise, if there are no revisions, it stops the watch
 */
export const fetchBuildWatchRevisions = (): ThunkAction<void, ReduxStore, never, AnyAction> =>
  async (dispatch, getState) => {
    const state = getState();
    const revisions = getWatchedRevisions(state);

    if (revisions.length === 0) {
      return dispatch(stopBuildWatchAction());
    }

    return dispatch(fetchBuildWatchRevisionsAction(revisions));
  };

/**
 * Action to fetch revisions for build watch
 */
export const fetchBuildWatchRevisionsAction = (revisions: string[]): ReduxAction => ({
  type: actionTypes.BUILD_WATCH_FETCH_REVISIONS,
  payload: async () => get(endpoints.revisions, true, { locator: revisions }, { encodeQuery: true }),
});

/**
 * Thunk to add a new revision and fetch data - will start a watch if there is no interval
 */
export const addAndFetchBuildWatchRevision = (
  revision: string, shouldWatch: boolean = true, interval: number = DEFAULT_TIMEOUT,
): ThunkAction<void, ReduxStore, never, AnyAction> =>
  async (dispatch) => {
    await dispatch(addRevisionToBuildWatch(revision));
    await dispatch(fetchBuildWatchRevisions());

    if (shouldWatch) {
      await dispatch(startBuildWatch(interval));
    }
  };
